import React, { useCallback, useEffect, useRef, useState } from 'react'
import { ArrowLeft, BarChart3, LogOut, Sparkles, AlertTriangle, Clock3, ListChecks } from 'lucide-react'
import { doc, getDoc } from 'firebase/firestore'
import { apiFetch } from './apiClient'
import { db } from './firebase'

// Parse the AI response, preferring structured JSON and falling back to regex parsing
const parseInsight = (insightText) => {
  if (!insightText) return null

  const tryJson = () => {
    try {
      const cleaned =
        typeof insightText === 'string'
          ? insightText
              .trim()
              .replace(/^```json\s*/i, '')
              .replace(/^```\s*/i, '')
              .replace(/```$/, '')
          : insightText
      const parsed = typeof cleaned === 'string' ? JSON.parse(cleaned) : cleaned
      if (parsed && typeof parsed === 'object') {
        return {
          overview: parsed.market_overview || parsed.overview || [],
          technicals: parsed.technical_indicators || parsed.technicals || [],
          fundamentals: parsed.fundamental_indicators || parsed.fundamentals || [],
          signals: parsed.signals || [],
          risks: parsed.risks || parsed.risks_to_consider || [],
          disclaimer: parsed.disclaimer || ''
        }
      }
    } catch (err) {
      return null
    }
    return null
  }

  const jsonParsed = tryJson()
  if (jsonParsed) return jsonParsed

  // Legacy text parsing fallback
  const text = insightText.replace(/\r\n/g, '\n')
  const overviewLabel = /Market Overview\s*:\s*/i
  const signalLabel = /Signal\s*:\s*/i
  const risksLabel = /Risks to consider\s*:\s*/i

  const signalIndex = text.search(signalLabel)
  const risksMatch = text.match(risksLabel)
  const risksIndex = risksMatch ? risksMatch.index : -1

  let overview = ''
  const overviewMatch = text.match(overviewLabel)
  if (overviewMatch) {
    const start = overviewMatch.index + overviewMatch[0].length
    const end = signalIndex !== -1 ? signalIndex : risksIndex !== -1 ? risksIndex : text.length
    overview = text.slice(start, end).trim()
  }

  let risks = ''
  if (risksMatch) {
    const start = risksMatch.index + risksMatch[0].length
    risks = text.slice(start).trim()
  }

  let signals = []
  if (signalIndex !== -1) {
    const signalSectionEnd = risksIndex !== -1 ? risksIndex : text.length
    const signalBlock = text.slice(signalIndex, signalSectionEnd)
    signals = signalBlock
      .split(/Signal\s*:\s*/i)
      .map((block) => block.trim())
      .filter(Boolean)
      .map((block) => {
        const parts = block.split(/,\s*/)
        const asset = parts.shift() || 'Signal'
        const decision = parts.shift() || ''
        const rationale = parts.join(', ').trim() || block
        return { asset, decision, rationale }
      })
  }

  if (!overview && !signals.length && !risks) return null
  return { overview, signals, risks, technicals: [], fundamentals: [], disclaimer: '' }
}

const toPoints = (value) => {
  if (!value) return []
  if (Array.isArray(value)) {
    return value.map((v) => (typeof v === 'string' ? v.trim() : '')).filter(Boolean)
  }
  const text = String(value)
  const sentences = text.match(/[^.!?]+[.!?]?/g)
  if (sentences && sentences.length > 1) {
    return sentences.map((s) => s.trim()).filter(Boolean)
  }
  return text
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
}

function OutputPage({ onNavigate, user, onLogout }) {
  const [portfolio, setPortfolio] = useState([])
  const [aiInsight, setAiInsight] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [lastUpdated, setLastUpdated] = useState(null)
  const userInitial = user?.displayName?.[0]?.toUpperCase() || 'N'
  const hasSyncedPortfolio = useRef(false)
  const parsedInsight = parseInsight(aiInsight)

  const buildPortfolioSummary = (items) => {
    const tickersList = items.map((stock) => stock.ticker || stock.name || '').filter(Boolean).join(', ')
    const header = `All Stocks: ${tickersList || 'N/A'}`
    const perStockDetails = items
      .map((stock) => {
        const name = stock.ticker || stock.name || 'Unknown'
        const quantity = Number(stock.quantity || 0)
        const price = Number(stock.price || 0)
        const change = Number(stock.change || 0)
        return `${name}: ${quantity} shares @ ${price} (${change >= 0 ? '+' : ''}${change.toFixed(1)}%)`
      })
      .join('; ')
    return `${header}; ${perStockDetails}`
  }

  const syncPortfolioFromFirestore = useCallback(async () => {
    if (!user?.uid || hasSyncedPortfolio.current) return false
    try {
      const snapshot = await getDoc(doc(db, 'users', user.uid))
      const data = snapshot.exists() ? snapshot.data() : null
      const items = data?.lastPortfolio?.items
      if (!Array.isArray(items) || items.length === 0) return false
      const summary = buildPortfolioSummary(items)
      await apiFetch('/save-portfolio', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: summary })
      })
      setPortfolio([summary])
      hasSyncedPortfolio.current = true
      return true
    } catch (err) {
      console.warn('Could not sync portfolio from Firestore:', err)
      hasSyncedPortfolio.current = true
      return false
    }
  }, [user])

  const fetchPortfolio = useCallback(async () => {
    try {
      const res = await apiFetch('/portfolio')
      const data = await res.json()
      if (data.portfolio?.length) {
        setPortfolio(data.portfolio)
        if (data.insight) setAiInsight(data.insight)
        return
      }

      const didSync = await syncPortfolioFromFirestore()
      if (didSync) {
        const retry = await apiFetch('/portfolio')
        const retryData = await retry.json()
        if (retryData.portfolio) setPortfolio(retryData.portfolio)
        if (retryData.insight) setAiInsight(retryData.insight)
        return
      }

      if (data.portfolio) setPortfolio(data.portfolio)
      if (data.insight) setAiInsight(data.insight)
    } catch (err) {
      console.error('Error fetching portfolio:', err)
    } finally {
      setIsLoading(false)
      setLastUpdated(new Date())
    }
  }, [syncPortfolioFromFirestore])

  const handleGenerateReport = async () => {
    setIsLoading(true)
    setAiInsight('') // Clear old report immediately
    await fetchPortfolio()
  }

  useEffect(() => {
    fetchPortfolio() // initial fetch (loads both portfolio and insight)
  }, [fetchPortfolio])

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Navigation */}
      <nav className="relative z-10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <button 
            onClick={() => onNavigate && onNavigate('home')} 
            className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-700 via-fuchsia-600 to-purple-500"
          >
            Neo
          </button>
          <div className="flex items-center gap-4">
            {user && (
              <div className="hidden sm:flex items-center gap-2 bg-white border border-purple-100 rounded-full px-3 py-1 shadow-sm">
                {user.photoURL ? (
                  <img src={user.photoURL} alt={user.displayName || 'User'} className="w-8 h-8 rounded-full border border-purple-200" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-fuchsia-500 text-white font-bold flex items-center justify-center">
                    {userInitial}
                  </div>
                )}
                <div>
                  <p className="text-xs text-purple-500 leading-none">Signed in</p>
                  <p className="text-sm text-slate-900 font-semibold leading-none truncate max-w-[140px]">{user.displayName || user.email}</p>
                </div>
              </div>
            )}
            <button 
              onClick={() => onNavigate && onNavigate('portfolio')} 
              className="hidden sm:inline-flex items-center gap-2 bg-white border border-purple-100 text-purple-700 px-4 py-2 rounded-lg hover:shadow-md transition"
            >
              Portfolio
            </button>
            <button 
              onClick={() => onNavigate && onNavigate('bubble')} 
              className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-700 hover:to-fuchsia-700 text-white px-4 py-2 rounded-lg transition shadow-sm"
            >
              <BarChart3 className="w-4 h-4" />
              Back to Market Visualization
            </button>
            <button 
              onClick={() => onNavigate && onNavigate('home')} 
              className="text-purple-700 hover:text-purple-900 transition-colors flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Home
            </button>
            {onLogout && (
              <button
                onClick={onLogout}
                className="flex items-center gap-2 bg-white border border-purple-100 text-purple-700 px-4 py-2 rounded-lg transition hover:shadow-md"
              >
                <LogOut className="w-4 h-4" />
                Log out
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <section className="px-6 py-20">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold text-slate-900 mb-6">
              Portfolio Analysis
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-700 via-fuchsia-600 to-purple-500">
                Institutional-grade insights
              </span>
            </h1>
            <p className="text-xl text-slate-700">
              Generate a risk-aware report on demand for your LPs and investment committee.
            </p>
          </div>

          {/* Portfolio Display */}
          <div className="mb-8">
            <div className="bg-white rounded-2xl p-8 border border-purple-100 shadow-lg">
              <h3 className="text-slate-900 font-semibold mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-purple-500" />
                Your Portfolio
              </h3>
              {isLoading ? (
                <div className="text-slate-500 italic text-center py-8">
                  Loading portfolio...
                </div>
              ) : portfolio.length > 0 ? (
                <ul className="list-disc list-inside text-slate-800 space-y-1">
                  {portfolio.map((item, idx) => (
                    <li key={idx} className="leading-relaxed">{item}</li>
                  ))}
                </ul>
              ) : (
                <div className="text-slate-500 italic text-center py-8">
                  No portfolio entries saved yet. Visit the Bubble Chart to build your portfolio.
                </div>
              )}
            </div>
          </div>

          {/* AI Insight Display */}
          <div>
            <div className="bg-white rounded-2xl p-8 border border-purple-100 shadow-lg">
              <h3 className="text-slate-900 font-semibold mb-4 flex items-center gap-2">
                <span className="text-2xl">🤖</span>
                AI Market Intelligence
              </h3>
              <div className="mb-4 flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-2 text-slate-600 text-sm">
                  <Clock3 className="w-4 h-4" />
                  {lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString()}` : 'Awaiting first run'}
                </div>
                <button
                  onClick={handleGenerateReport}
                  disabled={isLoading || portfolio.length === 0}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${isLoading || portfolio.length === 0 ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white hover:from-purple-700 hover:to-fuchsia-700 shadow-sm'}`}
                >
                  {isLoading ? 'Generating...' : 'Generate Report'}
                </button>
              </div>
              {isLoading ? (
                <div className="text-slate-500 italic text-center py-8">
                  🤔 AI is thinking...
                </div>
              ) : aiInsight ? (
                parsedInsight ? (
                  <div className="space-y-6">
                    {toPoints(parsedInsight.overview).length > 0 && (
                      <div className="rounded-xl border border-purple-100 bg-white p-5 shadow-sm">
                        <div className="flex items-center gap-2 text-purple-700 font-semibold mb-3">
                          <Sparkles className="w-4 h-4" />
                          Market Overview
                        </div>
                        <ul className="space-y-3">
                          {toPoints(parsedInsight.overview).map((point, idx) => (
                            <li key={idx} className="flex gap-3 text-slate-800 leading-relaxed">
                              <span className="mt-2 h-2 w-2 rounded-full bg-purple-500 flex-shrink-0" />
                              <p>{point}</p>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {parsedInsight.technicals?.length > 0 && (
                      <div className="rounded-xl border border-purple-100 bg-purple-50 p-5 shadow-sm">
                        <div className="flex items-center gap-2 text-purple-700 font-semibold mb-3">
                          <ListChecks className="w-4 h-4" />
                          Technical Indicators
                        </div>
                        <div className="space-y-4">
                          {parsedInsight.technicals.map((item, idx) => (
                            <div key={idx} className="rounded-lg border border-purple-100 bg-white p-4 shadow-sm">
                              <div className="flex items-center gap-2 mb-3">
                                <span className="px-2 py-1 rounded-md bg-purple-100 text-purple-700 text-xs font-semibold">
                                  {item.ticker || 'Asset'}
                                </span>
                                {item.note && (
                                  <span className="text-xs text-slate-600 italic">{item.note}</span>
                                )}
                              </div>
                              {toPoints(item.highlights).length > 0 && (
                                <ul className="space-y-2 text-slate-800">
                                  {toPoints(item.highlights).map((pt, i) => (
                                    <li key={i} className="flex gap-2">
                                      <span className="mt-2 h-2 w-2 rounded-full bg-purple-500 flex-shrink-0" />
                                      <span>{pt}</span>
                                    </li>
                                  ))}
                                </ul>
                              )}
                              {toPoints(item.notable_levels).length > 0 && (
                                <div className="mt-3 text-sm text-slate-700">
                                  <span className="font-semibold">Levels:</span> {toPoints(item.notable_levels).join('; ')}
                                </div>
                              )}
                              {toPoints(item.risks).length > 0 && (
                                <div className="mt-3 text-sm text-red-700">
                                  <span className="font-semibold">Risks:</span> {toPoints(item.risks).join('; ')}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {parsedInsight.fundamentals?.length > 0 && (
                      <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-5 shadow-sm">
                        <div className="flex items-center gap-2 text-emerald-700 font-semibold mb-3">
                          <ListChecks className="w-4 h-4" />
                          Fundamental Indicators
                        </div>
                        <div className="space-y-4">
                          {parsedInsight.fundamentals.map((item, idx) => (
                            <div key={idx} className="rounded-lg border border-emerald-100 bg-white p-4 shadow-sm">
                              <div className="flex items-center gap-2 mb-3">
                                <span className="px-2 py-1 rounded-md bg-emerald-100 text-emerald-700 text-xs font-semibold">
                                  {item.ticker || 'Asset'}
                                </span>
                                {item.note && (
                                  <span className="text-xs text-slate-600 italic">{item.note}</span>
                                )}
                              </div>
                              {toPoints(item.strengths || item.highlights).length > 0 && (
                                <ul className="space-y-2 text-slate-800">
                                  {toPoints(item.strengths || item.highlights).map((pt, i) => (
                                    <li key={i} className="flex gap-2">
                                      <span className="mt-2 h-2 w-2 rounded-full bg-emerald-500 flex-shrink-0" />
                                      <span>{pt}</span>
                                    </li>
                                  ))}
                                </ul>
                              )}
                              {toPoints(item.valuation).length > 0 && (
                                <div className="mt-3 text-sm text-slate-700">
                                  <span className="font-semibold">Valuation:</span> {toPoints(item.valuation).join('; ')}
                                </div>
                              )}
                              {toPoints(item.insider_activity).length > 0 && (
                                <div className="mt-3 text-sm text-slate-700">
                                  <span className="font-semibold">Insider:</span> {toPoints(item.insider_activity).join('; ')}
                                </div>
                              )}
                              {toPoints(item.weaknesses || item.risks).length > 0 && (
                                <div className="mt-3 text-sm text-red-700">
                                  <span className="font-semibold">Risks:</span> {toPoints(item.weaknesses || item.risks).join('; ')}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {parsedInsight.signals?.length > 0 && (
                      <div className="rounded-xl border border-purple-100 bg-gradient-to-r from-purple-50 via-white to-fuchsia-50 p-5 shadow-sm">
                        <div className="flex items-center gap-2 text-purple-700 font-semibold mb-3">
                          <ListChecks className="w-4 h-4" />
                          Signals & Rationale
                        </div>
                        <div className="grid md:grid-cols-2 gap-4">
                          {parsedInsight.signals.map((signal, idx) => (
                            <div key={idx} className="rounded-lg border border-purple-100 bg-white p-4 flex flex-col gap-2 shadow-sm">
                              <div className="flex items-center gap-2">
                                <span className="px-2 py-1 rounded-md bg-purple-100 text-purple-700 text-xs font-semibold">
                                  {signal.asset}
                                </span>
                                {signal.decision && (
                                  <span className="px-2 py-1 rounded-md bg-purple-600 text-white text-xs font-semibold">
                                    {signal.decision}
                                  </span>
                                )}
                              </div>
                              <p className="text-slate-800 leading-relaxed">{signal.rationale}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {toPoints(parsedInsight.risks).length > 0 && (
                      <div className="rounded-xl border border-red-100 bg-red-50 p-5 shadow-sm">
                        <div className="flex items-center gap-2 text-red-700 font-semibold mb-3">
                          <AlertTriangle className="w-4 h-4" />
                          Risks to Consider
                        </div>
                        <ul className="space-y-3">
                          {toPoints(parsedInsight.risks).map((risk, idx) => (
                            <li key={idx} className="flex gap-3 text-red-800 leading-relaxed">
                              <span className="mt-2 h-2 w-2 rounded-full bg-red-500 flex-shrink-0" />
                              <p>{risk}</p>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {parsedInsight.disclaimer && (
                      <div className="text-xs text-slate-500 text-center italic">
                        {parsedInsight.disclaimer}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-gradient-to-r from-purple-50 to-fuchsia-50 rounded-lg p-6 border border-purple-100">
                    <p className="text-slate-800 text-lg leading-relaxed whitespace-pre-line">{aiInsight}</p>
                  </div>
                )
              ) : (
                <div className="text-slate-500 italic text-center py-8">
                  No insights available. Add stocks to your portfolio for AI analysis.
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default OutputPage
